
import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Music, Users, Zap, Target, Award, Heart } from 'react-feather';

const AboutPage: React.FC = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="hero-section py-5" style={{ background: 'linear-gradient(135deg, #6C63FF 0%, #4F46E5 100%)' }}>
        <Container>
          <Row className="text-center text-white">
            <Col>
              <h1 className="display-4 fw-bold mb-4">Revolutionizing the DJ Experience</h1>
              <p className="lead mb-4">
                Empowering DJs worldwide to create deeper connections with their audience through innovative technology
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Mission Section */}
      <section className="mission-section py-5">
        <Container>
          <Row className="align-items-center mb-5">
            <Col lg={6}>
              <h2 className="display-5 fw-bold mb-4">Our Mission</h2>
              <p className="lead mb-4">
                At MyWeeshy, we believe that the connection between a DJ and their audience is sacred. 
                We've witnessed countless moments where a single song request can transform an entire night, 
                where the perfect track at the right moment creates memories that last a lifetime.
              </p>
              <p className="mb-4">
                Traditional methods of handling song requests—shouting over loud music, scribbling on napkins, 
                or trying to catch a DJ's attention—break this magical connection. We created MyWeeshy to bridge 
                this gap, enabling seamless communication that enhances rather than disrupts the flow of any event.
              </p>
              <div className="d-flex align-items-center">
                <Music className="text-primary me-3" size={32} />
                <div>
                  <h5 className="mb-1">Preserving the Art of DJing</h5>
                  <p className="text-muted mb-0">While embracing modern technology</p>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="text-center">
                <div className="bg-light rounded p-5">
                  <h3 className="fw-bold mb-4">Why DJs Need MyWeeshy</h3>
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="bg-white rounded p-3 shadow-sm">
                        <h4 className="text-primary fw-bold">89%</h4>
                        <small>of club-goers want to request songs</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-white rounded p-3 shadow-sm">
                        <h4 className="text-success fw-bold">73%</h4>
                        <small>never get their requests heard</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-white rounded p-3 shadow-sm">
                        <h4 className="text-warning fw-bold">45%</h4>
                        <small>would tip for played requests</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="bg-white rounded p-3 shadow-sm">
                        <h4 className="text-danger fw-bold">91%</h4>
                        <small>prefer digital request systems</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Problem & Solution */}
      <section className="problem-solution py-5 bg-light">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">The Challenge DJs Face Today</h2>
              <p className="lead text-muted">Understanding the gap between DJs and their audience</p>
            </Col>
          </Row>
          
          <Row className="g-4 mb-5">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="text-danger mb-3">
                    <Target size={48} />
                  </div>
                  <h4 className="fw-bold mb-3">The Problems</h4>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex">
                      <span className="text-danger me-2">✗</span>
                      <span>Audience requests get lost in noise and chaos</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-danger me-2">✗</span>
                      <span>DJs can't focus on mixing while managing requests</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-danger me-2">✗</span>
                      <span>No organized system for request management</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-danger me-2">✗</span>
                      <span>Missed opportunities for tips and audience engagement</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-danger me-2">✗</span>
                      <span>Frustrated guests leave early or don't return</span>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="text-success mb-3">
                    <Zap size={48} />
                  </div>
                  <h4 className="fw-bold mb-3">Our Solution</h4>
                  <ul className="list-unstyled">
                    <li className="mb-3 d-flex">
                      <span className="text-success me-2">✓</span>
                      <span>Instant digital requests via QR codes</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-success me-2">✓</span>
                      <span>Real-time request queue management</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-success me-2">✓</span>
                      <span>Integrated tipping system for requests</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-success me-2">✓</span>
                      <span>Professional DJ profiles and branding</span>
                    </li>
                    <li className="mb-3 d-flex">
                      <span className="text-success me-2">✓</span>
                      <span>Analytics to understand audience preferences</span>
                    </li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why It's Needed */}
      <section className="why-needed py-5">
        <Container>
          <Row className="text-center mb-5">
            <Col>
              <h2 className="display-5 fw-bold mb-3">Why the Industry Needs MyWeeshy</h2>
              <p className="lead text-muted">The digital transformation of live entertainment</p>
            </Col>
          </Row>
          
          <Row className="g-4">
            <Col md={4}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Users className="text-primary mb-3" size={48} />
                  <h4 className="fw-bold mb-3">Audience Expectations</h4>
                  <p className="text-muted">
                    Modern audiences expect interactive, personalized experiences. They want to feel 
                    heard and connected to the music that moves them. MyWeeshy delivers this connection 
                    seamlessly.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Award className="text-warning mb-3" size={48} />
                  <h4 className="fw-bold mb-3">Professional Growth</h4>
                  <p className="text-muted">
                    DJs using our platform report 40% higher audience satisfaction, increased booking 
                    requests, and significant growth in tips and repeat engagements at venues.
                  </p>
                </Card.Body>
              </Card>
            </Col>
            
            <Col md={4}>
              <Card className="text-center h-100 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <Heart className="text-danger mb-3" size={48} />
                  <h4 className="fw-bold mb-3">Industry Evolution</h4>
                  <p className="text-muted">
                    The entertainment industry is evolving rapidly. DJs who embrace technology while 
                    maintaining their artistic integrity are the ones who thrive in today's market.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Team Vision */}
      <section className="team-vision py-5" style={{ backgroundColor: '#1C2333' }}>
        <Container>
          <Row className="text-center text-white">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Built by DJs, for DJs</h2>
              <p className="lead mb-4">
                Our team consists of professional DJs, software engineers, and music industry veterans 
                who understand the unique challenges of live performance. We don't just build software—we 
                craft solutions that respect the art of DJing while embracing the power of technology.
              </p>
              <p className="mb-4">
                From intimate club sets to massive festival stages, from wedding receptions to corporate 
                events, MyWeeshy adapts to every environment while maintaining the same core principle: 
                strengthening the bond between DJs and their audiences.
              </p>
              <Row className="justify-content-center mt-5">
                <Col md={8}>
                  <div className="bg-primary bg-opacity-10 rounded p-4">
                    <h3 className="fw-bold mb-3">"The future of DJing is interactive"</h3>
                    <p className="mb-0">
                      We envision a world where every DJ has the tools to create unforgettable experiences, 
                      where every audience member feels heard, and where technology enhances rather than 
                      replaces the human connection that makes live music magical.
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section py-5">
        <Container>
          <Row className="text-center">
            <Col>
              <h2 className="display-5 fw-bold mb-4">Join the Revolution</h2>
              <p className="lead mb-4">
                Be part of the next generation of DJs who are redefining audience engagement
              </p>
              <div className="d-flex flex-column flex-sm-row gap-3 justify-content-center">
                <Link to="/signup" className="text-decoration-none">
                  <Button 
                    size="lg" 
                    className="px-4"
                    style={{ 
                      backgroundColor: '#6C63FF', 
                      borderColor: '#6C63FF',
                      color: 'white'
                    }}
                  >
                    Start Your Journey
                  </Button>
                </Link>
                <Link to="/features" className="text-decoration-none">
                  <Button 
                    variant="outline-primary" 
                    size="lg"
                    className="px-4"
                  >
                    Explore Features
                  </Button>
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default AboutPage;
